import type { AxiosHeaders, AxiosResponse, RawAxiosResponseHeaders } from 'axios';

import type { OrganizationAdminResponseQuestion } from 'types';

export const RESPONSE_TYPE_LABELS: Record<string, string> = {
  rating: 'Rating',
  multiple_choice: 'Multiple Choice',
  multiple_select: 'Multiple Select',
  boolean: 'Yes / No',
  drag_and_drop_ranking: 'Ranking',
  text_or_audio: 'Text / Audio',
  callback_summary: 'Callback Summary',
  text_list: 'Text List',
};

const RESPONSE_TYPE_BADGE_STYLES: Record<string, string> = {
  rating: 'border-foundation-orange-100 bg-foundation-orange-50 text-foundation-orange-900',
  multiple_choice: 'border-foundation-calm-blue-200 bg-foundation-calm-blue-50 text-foundation-calm-blue-700',
  multiple_select: 'border-primary/20 bg-primary/10 text-primary',
  boolean: 'border-foundation-base-darkbase-dark-100 bg-foundation-base-darkbase-dark-50 text-foundation-base-darkbase-dark-500',
  drag_and_drop_ranking: 'border-foundation-purplepurple-100 bg-foundation-purplepurple-100 text-foundation-purplepurple-900',
  text_or_audio: 'border-foundation-orange-100 bg-orange-50 text-foundation-orange-500',
  callback_summary: 'border-foundation-purplepurple-100 bg-purple-50 text-foundation-purplepurple-500',
  text_list: 'border-foundation-calm-blue-200 bg-sky-50 text-sky-700',
};

export const getTypeLabel = (type: string) => RESPONSE_TYPE_LABELS[type] ?? type;

export const getQuestionTypeBadgeClassName = (type: string) => (
  RESPONSE_TYPE_BADGE_STYLES[type] ?? 'border-foundation-base-darkbase-dark-100 bg-white text-foundation-base-darkbase-dark-500'
);

export const getQuestionContext = (question: OrganizationAdminResponseQuestion) => {
  const courseNames = question.courses?.map((course) => course.name).filter(Boolean) ?? [];
  const moduleNames = question.modules?.map((module) => module.name).filter(Boolean) ?? question.flow_names ?? [];
  if (courseNames.length && moduleNames.length) {
    return `${courseNames.join(' | ')} - ${moduleNames.join(' | ')}`;
  }
  if (courseNames.length) {
    return courseNames.join(' | ');
  }
  if (moduleNames.length) {
    return moduleNames.join(' | ');
  }
  if (question.flow_names?.length) {
    return question.flow_names.join(' | ');
  }
  return question.category || question.key;
};

export const getFilenameFromContentDisposition = (header?: string | null) => {
  if (!header) {
    return null;
  }
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }
  const unquotedMatch = header.match(/filename=([^;]+)/i);
  if (unquotedMatch?.[1]) {
    return unquotedMatch[1].trim();
  }
  return null;
};

export const getContentDispositionHeader = (
  headers?: RawAxiosResponseHeaders | AxiosHeaders,
) => {
  if (!headers) {
    return null;
  }
  if (typeof (headers as AxiosHeaders).get === 'function') {
    const headerValue = (headers as AxiosHeaders).get('content-disposition');
    return typeof headerValue === 'string' ? headerValue : null;
  }
  const headerValue = (headers as RawAxiosResponseHeaders)['content-disposition'];
  if (Array.isArray(headerValue)) {
    return headerValue.join('; ');
  }
  return typeof headerValue === 'string' ? headerValue : null;
};

export const sanitizeFilenamePart = (value: string) =>
  Array.from(value)
    .filter((character) => character.charCodeAt(0) >= 32)
    .join('')
    .replace(/[<>:"/\\|?*]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const buildExportFilename = (...parts: string[]) =>
  parts
    .map(sanitizeFilenamePart)
    .filter(Boolean)
    .join(' - ');

export const getExportDate = () => new Date().toISOString().slice(0, 10);

export const triggerBlobDownload = (
  response: AxiosResponse<Blob>,
  fallbackFilename: string,
) => {
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'text/csv' });
  const filename = getFilenameFromContentDisposition(getContentDispositionHeader(response.headers)) || fallbackFilename;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
